import React from 'react';
import PropTypes from 'prop-types';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import style from './style';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const range = (from, to, step = 1) => {
    const result = [];
    for (let i = from; i <= to; i += step) {
        result.push(i);
    }
    return result;
};

class TablePagination extends React.Component {
    constructor(props) {
        super(props);

        const {
            totalRecords = 0,
            pageLimit = 20,
            pageNeighbours = 0,
            currentPage = 1,
        } = props;

        const safePageNeighbours = Math.max(0, Math.min(pageNeighbours, 2));

        this.state = {
            currentPage,
            pageLimit,
            totalRecords,
            pageNeighbours: safePageNeighbours,
            totalPages: Math.ceil(totalRecords / pageLimit),
        };
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.totalRecords !== this.props.totalRecords ||
            prevProps.pageLimit !== this.props.pageLimit
        ) {
            this.setState({
                totalRecords: this.props.totalRecords,
                pageLimit: this.props.pageLimit,
                totalPages: Math.ceil(
                    this.props.totalRecords / this.props.pageLimit
                ),
            });
        }
    }

    fetchPageNumbers = () => {
        const { totalPages, currentPage, pageNeighbours } = this.state;

        const totalNumbers = pageNeighbours * 2 + 3;
        const totalBlocks = totalNumbers + 2;

        if (totalPages > totalBlocks) {
            const startPage = Math.max(2, currentPage - pageNeighbours);
            const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

            let pages = range(startPage, endPage);

            const hasLeftSpill = startPage > 2;
            const hasRightSpill = totalPages - endPage > 1;
            const spillOffset = totalNumbers - (pages.length + 1);

            if (hasLeftSpill && !hasRightSpill) {
                const extraPages = range(startPage - spillOffset, startPage - 1);
                pages = [LEFT_PAGE, ...extraPages, ...pages];
            } else if (!hasLeftSpill && hasRightSpill) {
                const extraPages = range(endPage + 1, endPage + spillOffset);
                pages = [...pages, ...extraPages, RIGHT_PAGE];
            } else if (hasLeftSpill && hasRightSpill) {
                pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
            }

            return [1, ...pages, totalPages];
        }

        return range(1, totalPages);
    };

    gotoPage = (page) => {
        const { onPageChanged = () => {} } = this.props;
        const { totalPages, pageLimit, totalRecords } = this.state;

        const currentPage = Math.max(1, Math.min(page, totalPages));

        this.setState({ currentPage }, () => {
            onPageChanged({
                currentPage,
                totalPages,
                pageLimit,
                totalRecords,
            });
        });
    };

    handleClick = (page) => (e) => {
        e.preventDefault();
        this.gotoPage(page);
    };

    handleMoveLeft = (e) => {
        e.preventDefault();
        this.gotoPage(
            this.state.currentPage - this.state.pageNeighbours * 2 - 1
        );
    };

    handleMoveRight = (e) => {
        e.preventDefault();
        this.gotoPage(
            this.state.currentPage + this.state.pageNeighbours * 2 + 1
        );
    };

    render() {
        const { totalRecords, totalPages, currentPage } = this.state;

        if (!totalRecords || totalPages === 1) return null;

        const pages = this.fetchPageNumbers();

        return (
            <Pagination style={style.pagination}>
                {pages.map((page, index) => {
                    if (page === LEFT_PAGE) {
                        return (
                            <PaginationItem key={index}>
                                <PaginationLink
                                    tag="button"
                                    previous
                                    onClick={this.handleMoveLeft}
                                >
                                    Prev
                                </PaginationLink>
                            </PaginationItem>
                        );
                    }

                    if (page === RIGHT_PAGE) {
                        return (
                            <PaginationItem key={index}>
                                <PaginationLink
                                    tag="button"
                                    next
                                    onClick={this.handleMoveRight}
                                >
                                    Next
                                </PaginationLink>
                            </PaginationItem>
                        );
                    }

                    return (
                        <PaginationItem
                            key={index}
                            active={currentPage === page}
                        >
                            <PaginationLink
                                tag="button"
                                onClick={this.handleClick(page)}
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    );
                })}
            </Pagination>
        );
    }
}

TablePagination.propTypes = {
    totalRecords: PropTypes.number,
    pageLimit: PropTypes.number,
    pageNeighbours: PropTypes.number,
    onPageChanged: PropTypes.func,
    currentPage: PropTypes.number,
};

export default TablePagination;
